import { AlertTriangle, CircleHelp, Lightbulb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ReadinessItem } from './translate-gaps'

/**
 * Extracted out of `readiness-panel.tsx` (Task 4) so a second surface can
 * render a `ReadinessItem` in the exact same visual language instead of
 * re-implementing (and inevitably drifting from) the same
 * icon/title/explanation/suggestion layout.
 *
 * Task 5 (ontology-ux-contract cycle) is that second surface:
 * `design-system/source-derived/axis-resolution-list.tsx` reuses this row to
 * render an axis gap (`translateAxisResolution`'s `kind:'unfilled'` output)
 * the same way `readiness-panel.tsx` renders a blocking machine gap or an
 * advisory — neither call site knows or cares which of the three gap
 * vocabularies produced the item it was handed.
 */

/**
 * Attention is the only tone that gets a colour — same rule as
 * `inventoryToneClassName` (`design-system/agentic/agentic-design-system-page.tsx`)
 * and the `AdviceRow` comment in `design-system/source-derived/axis-resolution-list.tsx`.
 * `blocking` is this row's genuine attention case: it is, structurally,
 * the one thing that stops generation, so it is the only kind allowed
 * `text-destructive`. `unfilled` ("you haven't decided this yet") and
 * `advisory` ("here's a heads up") are exactly the two cases
 * axis-resolution-list.tsx's own comment names as NOT deserving red — both
 * stay in the neutral `border-border`/`text-muted-foreground` pair here too.
 */
function readinessItemToneClassName(kind: ReadinessItem['kind']): string {
  return kind === 'blocking'
    ? 'border-destructive/30 text-destructive'
    : 'border-border text-muted-foreground'
}

function readinessItemIcon(kind: ReadinessItem['kind']) {
  if (kind === 'blocking') return AlertTriangle
  if (kind === 'advisory') return Lightbulb
  return CircleHelp
}

export function ReadinessItemRow({ item }: { item: ReadinessItem }) {
  const Icon = readinessItemIcon(item.kind)
  return (
    <li
      data-testid="readiness-item"
      data-kind={item.kind}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
        readinessItemToneClassName(item.kind),
      )}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
        <p className="font-medium">{item.title}</p>
        <p className="leading-5 text-muted-foreground">{item.explanation}</p>
        <ReadinessItemSuggestion item={item} />
      </div>
    </li>
  )
}

/**
 * Many of the 53 machine-gap slots (ports/journeys/graph-shape codes in
 * particular) deliberately carry no `suggestion` — there is nowhere to send
 * the user and nothing to click, so this renders explanation-only, never a
 * dead/no-op button. When a `suggestion` exists but has no `navigateTo`
 * (every `translateAdvice` suggestion, e.g.) it is read as plain text for
 * the same reason — there is no destination and no apply-in-place action
 * wired yet. Only a `suggestion.navigateTo` becomes a real link, styled as
 * the primary control when the owning item is `blocking` (the "recovery is
 * primary" requirement) and as a lighter control otherwise.
 */
function ReadinessItemSuggestion({ item }: { item: ReadinessItem }) {
  if (!item.suggestion) return null
  if (item.suggestion.navigateTo) {
    return (
      <Button
        asChild
        size="sm"
        variant={item.kind === 'blocking' ? 'default' : 'outline'}
        className="mt-1"
      >
        <a href={item.suggestion.navigateTo}>{item.suggestion.label}</a>
      </Button>
    )
  }
  return <p className="mt-1 text-muted-foreground">{item.suggestion.label}</p>
}
